// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import {
  getProxyBaseURL,
  proxyFetchDelete,
  proxyFetchGet,
  proxyFetchPatch,
  proxyFetchPost,
} from '@/api/http';
import { getDesktopInstanceId } from '@/lib/desktopIdentity';

const BRIDGE_READY_EVENT = 'eigent-remote-control-bridge-ready';
const PENDING_COMMAND_REQUESTS_KEY =
  'eigent:remote-control-command-requests:v1';
const PENDING_COMMAND_REQUEST_TTL_MS = 24 * 60 * 60 * 1000;
const PENDING_COMMAND_REQUEST_LIMIT = 32;

let remoteControlBridgeConnected = false;
let remoteControlBridgeError: RemoteControlBridgeError | null = null;

export type RemoteControlBridgeError = {
  code: string;
  message: string;
  retryable: boolean;
};

type PendingCommandRequest = {
  fingerprint: string;
  requestId: string;
  createdAt: number;
};

let pendingCommandRequestsFallback: PendingCommandRequest[] = [];

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, stableValue(item)])
    );
  }
  return value;
}

function readPendingCommandRequests(): PendingCommandRequest[] {
  const now = Date.now();
  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(PENDING_COMMAND_REQUESTS_KEY) || '[]'
    );
    pendingCommandRequestsFallback = Array.isArray(parsed)
      ? parsed
          .filter(
            (item): item is PendingCommandRequest =>
              typeof item?.fingerprint === 'string' &&
              typeof item?.requestId === 'string' &&
              typeof item?.createdAt === 'number' &&
              now - item.createdAt <= PENDING_COMMAND_REQUEST_TTL_MS
          )
          .slice(-PENDING_COMMAND_REQUEST_LIMIT)
      : [];
  } catch {
    pendingCommandRequestsFallback = pendingCommandRequestsFallback
      .filter((item) => now - item.createdAt <= PENDING_COMMAND_REQUEST_TTL_MS)
      .slice(-PENDING_COMMAND_REQUEST_LIMIT);
  }
  return pendingCommandRequestsFallback;
}

function writePendingCommandRequests(items: PendingCommandRequest[]): void {
  pendingCommandRequestsFallback = items.slice(-PENDING_COMMAND_REQUEST_LIMIT);
  try {
    window.sessionStorage.setItem(
      PENDING_COMMAND_REQUESTS_KEY,
      JSON.stringify(pendingCommandRequestsFallback)
    );
  } catch {
    // The in-memory fallback still preserves identity within this page load.
  }
}

function createClientRequestId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join(
    ''
  );
}

function commandRequestIdentity(
  sessionId: string,
  body: Record<string, unknown>
): { fingerprint: string; requestId: string } {
  const fingerprint = JSON.stringify(
    stableValue({ session_id: sessionId, ...body })
  );
  const existing = readPendingCommandRequests().find(
    (item) => item.fingerprint === fingerprint
  );
  if (existing) {
    return { fingerprint, requestId: existing.requestId };
  }
  const requestId = createClientRequestId();
  writePendingCommandRequests([
    ...pendingCommandRequestsFallback,
    { fingerprint, requestId, createdAt: Date.now() },
  ]);
  return { fingerprint, requestId };
}

function clearCommandRequestIdentity(fingerprint: string): void {
  writePendingCommandRequests(
    readPendingCommandRequests().filter(
      (item) => item.fingerprint !== fingerprint
    )
  );
}

export function getRemoteControlDesktopInstanceId(): Promise<string> {
  return getDesktopInstanceId();
}

export function setRemoteControlBridgeConnected(
  connected: boolean,
  error?: RemoteControlBridgeError | null
): void {
  remoteControlBridgeConnected = connected;
  if (connected) {
    remoteControlBridgeError = null;
  } else if (error !== undefined) {
    remoteControlBridgeError = error;
  }
  window.dispatchEvent(
    new CustomEvent(BRIDGE_READY_EVENT, {
      detail: { connected, error: remoteControlBridgeError },
    })
  );
}

export function isRemoteControlBridgeConnected(): boolean {
  return remoteControlBridgeConnected;
}

export function getRemoteControlBridgeError(): RemoteControlBridgeError | null {
  return remoteControlBridgeError;
}

export function waitForRemoteControlBridgeConnected(
  timeoutMs = 2500
): Promise<boolean> {
  if (remoteControlBridgeConnected) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener(BRIDGE_READY_EVENT, onReady);
      resolve(remoteControlBridgeConnected);
    }, timeoutMs);

    const onReady = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const connected = Boolean(detail?.connected);
      if (!connected) {
        if (detail?.error?.retryable === false) {
          window.clearTimeout(timeout);
          window.removeEventListener(BRIDGE_READY_EVENT, onReady);
          resolve(false);
        }
        return;
      }
      window.clearTimeout(timeout);
      window.removeEventListener(BRIDGE_READY_EVENT, onReady);
      resolve(true);
    };

    window.addEventListener(BRIDGE_READY_EVENT, onReady);
  });
}

export async function getRemoteControlWebSocketUrl(path: string) {
  const base = (await getProxyBaseURL()) || window.location.origin;
  const url = new URL(path, base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

function getRemoteControlWebOrigin(): string {
  const baseUrl =
    import.meta.env.VITE_PROXY_URL || import.meta.env.VITE_BASE_URL;
  const fallbackBaseUrl =
    typeof baseUrl === 'string' && /^https?:\/\//.test(baseUrl)
      ? baseUrl
      : undefined;
  const origin =
    import.meta.env.VITE_REMOTE_CONTROL_WEB_ORIGIN ||
    import.meta.env.VITE_SITE_URL ||
    import.meta.env.VITE_WEB_APP_ORIGIN ||
    fallbackBaseUrl ||
    window.location.origin;
  return String(origin).replace(/\/$/, '');
}

function normalizeRemoteControlUrl(url: string): string {
  if (url.startsWith('/')) {
    return `${getRemoteControlWebOrigin()}${url}`;
  }
  return url;
}

export function parseRemoteControlLinkToken(url: string): string {
  try {
    const parsed = new URL(url, 'http://localhost');
    const fragmentToken = new URLSearchParams(
      parsed.hash.replace(/^#/, '')
    ).get('t');
    if (fragmentToken) {
      return fragmentToken;
    }
    return parsed.searchParams.get('t') || '';
  } catch {
    const [, hash = ''] = url.split('#', 2);
    const fragmentToken = new URLSearchParams(hash).get('t');
    if (fragmentToken) {
      return fragmentToken;
    }
    const [, query = ''] = url.split('?', 2);
    return new URLSearchParams(query.split('#', 1)[0]).get('t') || '';
  }
}

/**
 * The revoke endpoint is not idempotent: once a session is revoked or expired
 * server-side, a subsequent DELETE returns 410 (Gone), and a missing session
 * returns 404. In both cases the link is already dead, so callers should treat
 * the revoke as successful and clean up local state instead of stranding it.
 */
export function isRemoteControlAlreadyGoneError(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === 404 || status === 410;
}

function remoteLinkHeaders(linkToken?: string | null): Record<string, string> {
  if (!linkToken) {
    return {};
  }
  return { 'X-Remote-Control-Token': linkToken };
}

export interface RemoteControlCreateSessionInput {
  desktop_instance_id: string;
  space_id?: string;
  project_id?: string;
  active_task_id?: string;
  brain_session_id?: string;
  initial_project_id?: string;
  initial_task_id?: string;
  initial_history_id?: string;
  title?: string;
  expires_in_seconds?: number;
}

export interface RemoteControlCreateSessionOutput {
  session_id: string;
  url: string;
  expires_at: string;
  bridge_status: string;
  space_id?: string | null;
  space_name?: string | null;
  current_project_id?: string | null;
  current_task_id?: string | null;
  current_history_id?: string | null;
  current_brain_session_id?: string | null;
}

export interface RemoteControlSession {
  session_id: string;
  desktop_instance_id?: string;
  space_id?: string | null;
  space_name?: string | null;
  project_id?: string | null;
  active_task_id?: string | null;
  brain_session_id?: string | null;
  current_project_id?: string | null;
  current_task_id?: string | null;
  current_history_id?: string | null;
  current_brain_session_id?: string | null;
  title: string;
  status: string;
  bridge_status: 'online' | 'offline' | string;
  execution_mode: string;
  capabilities: Record<string, unknown>;
  created_at: string | null;
  expires_at: string;
}

export interface RemoteControlStep {
  step_id: number | string;
  task_id: string;
  project_id?: string | null;
  step: string;
  data: unknown;
  timestamp?: number | null;
}

export interface RemoteCanonicalEvent {
  event_id: string;
  project_id: string;
  run_id: string;
  run_sequence: number;
  run_version: number;
  cloud_cursor: number;
  event_type: string;
  payload: Record<string, unknown>;
  legacy_step: string | null;
  created_at: string;
  ingested_at: string;
}

export interface RemoteProjectSnapshot {
  project_id: string;
  current_cursor: number;
  runs: Array<{
    run_id: string;
    status: string;
    expected_next_run_sequence: number;
    updated_at: string;
  }>;
  recent_events: RemoteCanonicalEvent[];
  artifact_events?: RemoteCanonicalEvent[];
  events_truncated: boolean;
}

export interface RemoteControlCommandResponse {
  command_id: string;
  status: string;
  next_task_id?: string | null;
}

export async function createRemoteControlSession(
  input: RemoteControlCreateSessionInput
): Promise<RemoteControlCreateSessionOutput> {
  const res = await proxyFetchPost('/api/v1/remote-control/sessions', input);
  return { ...res, url: normalizeRemoteControlUrl(res.url) };
}

export async function getRemoteControlSession(
  sessionId: string,
  linkToken: string
): Promise<RemoteControlSession> {
  return proxyFetchGet(
    `/api/v1/remote-control/sessions/${sessionId}`,
    undefined,
    remoteLinkHeaders(linkToken)
  );
}

export async function extendRemoteControlSession(
  sessionId: string,
  extendSeconds: number,
  linkToken?: string | null
): Promise<{ expires_at: string }> {
  return proxyFetchPost(
    `/api/v1/remote-control/sessions/${sessionId}/extend`,
    {
      extend_seconds: extendSeconds,
    },
    remoteLinkHeaders(linkToken)
  );
}

export async function revokeRemoteControlSession(
  sessionId: string,
  linkToken?: string | null
): Promise<void> {
  await proxyFetchDelete(
    `/api/v1/remote-control/sessions/${sessionId}`,
    undefined,
    remoteLinkHeaders(linkToken)
  );
}

export async function listRemoteControlSteps(
  sessionId: string,
  linkToken: string,
  since = 0,
  limit = 200,
  projectId?: string | null
): Promise<{
  items: RemoteControlStep[];
  has_more: boolean;
  next_since: number;
}> {
  return proxyFetchGet(
    `/api/v1/remote-control/sessions/${sessionId}/steps`,
    {
      ...(projectId ? { project_id: projectId } : {}),
      since,
      limit,
      order: 'asc',
    },
    remoteLinkHeaders(linkToken)
  );
}

export async function getRemoteControlSnapshot(
  sessionId: string,
  linkToken: string,
  projectId?: string | null,
  eventLimit = 1000
): Promise<RemoteProjectSnapshot> {
  return proxyFetchGet(
    `/api/v1/remote-control/sessions/${sessionId}/snapshot`,
    {
      ...(projectId ? { project_id: projectId } : {}),
      event_limit: eventLimit,
    },
    remoteLinkHeaders(linkToken)
  );
}

export async function listRemoteControlEvents(
  sessionId: string,
  linkToken: string,
  afterCursor: number,
  limit = 1000,
  projectId?: string | null
): Promise<{
  project_id: string;
  current_cursor: number;
  next_cursor: number;
  has_more: boolean;
  items: RemoteCanonicalEvent[];
}> {
  return proxyFetchGet(
    `/api/v1/remote-control/sessions/${sessionId}/events`,
    {
      ...(projectId ? { project_id: projectId } : {}),
      after_cursor: afterCursor,
      limit,
    },
    remoteLinkHeaders(linkToken)
  );
}

export async function sendRemoteControlCommand(
  sessionId: string,
  type: string,
  payload: Record<string, unknown>,
  target?: {
    project_id?: string | null;
    task_id?: string | null;
    brain_session_id?: string | null;
  },
  linkToken?: string | null
): Promise<RemoteControlCommandResponse> {
  const commandBody = {
    type,
    payload,
    source_channel: 'remote_web',
    target_project_id: target?.project_id,
    target_task_id: target?.task_id,
    target_brain_session_id: target?.brain_session_id,
  };
  const identity = commandRequestIdentity(sessionId, commandBody);
  const response = await proxyFetchPost(
    `/api/v1/remote-control/sessions/${sessionId}/commands`,
    {
      ...commandBody,
      client_request_id: identity.requestId,
    },
    remoteLinkHeaders(linkToken)
  );
  clearCommandRequestIdentity(identity.fingerprint);
  return response;
}

export const __remoteControlTestHooks = {
  resetPendingCommandRequests() {
    pendingCommandRequestsFallback = [];
    try {
      window.sessionStorage.removeItem(PENDING_COMMAND_REQUESTS_KEY);
    } catch {
      // no-op
    }
  },
};

export async function patchRemoteControlTarget(
  sessionId: string,
  input: {
    project_id: string;
    task_id?: string | null;
    history_id?: string | null;
  },
  linkToken?: string | null
): Promise<{
  current_project_id: string;
  current_task_id?: string | null;
  current_history_id?: string | null;
  current_brain_session_id: string;
}> {
  return proxyFetchPatch(
    `/api/v1/remote-control/sessions/${sessionId}/target`,
    input,
    remoteLinkHeaders(linkToken)
  );
}
